<script setup lang="ts">
import { ref, computed, onMounted } from 'vue';
import { Plus } from 'lucide-vue-next';
// shadcn-vue 컴포넌트 임포트
import { Button } from '@/components/shadcnui/button';
import { Input } from '@/components/shadcnui/input';
import { Label } from '@/components/shadcnui/label';
import { Skeleton } from '@/components/shadcnui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/shadcnui/tooltip';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/shadcnui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/shadcnui/dialog';
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem, PaginationNext, PaginationPrevious } from '@/components/shadcnui/pagination';
// composable 및 store 임포트
import { useRSStore } from '@/store/useRSStore';
import { useUserStore } from '@/store/useUserStore';
import { usePriority } from '@/composable/usePriority';
// 타입 임포트
import type { priority, addpriority } from '@/types/types';

// 스토어 및 composable 초기화
const RSStore = useRSStore();
const UserStore = useUserStore();
const { onReadPriorityList, onAddPriority, onUpdatePriority, onDeletePriority, onPaginationPriority } = usePriority();

// 상태 관리
const isDialogOpen = ref(false);
const isUpdateMode = ref(false);
const currentPage = ref(1);
const totalpage = ref(1);
const formData = ref<priority>({ id: 0, name: "", level: 0});
const isLoading = ref(true);
const isFormInvalid = computed(() => {
  return !formData.value.name?.trim() || !formData.value.level;
});

// 폼 데이터 초기화 함수
const formDataReset = () => {
  formData.value = { id: 0, name: "", level: 0 };
};

// 버튼 핸들러
const openAddDialog = () => {

  isUpdateMode.value = false;
  formDataReset();
  isDialogOpen.value = true;
};
const openUpdateDialog = (priority: priority) => {
  if (!UserStore.priorityEdit && !UserStore.priorityDelete) {
    return;
  }
  isUpdateMode.value = true;
  formData.value = { ...priority };
  isDialogOpen.value = true;
};
const cancelDialog = () => {
  isDialogOpen.value = false;
  formDataReset();
};

// ADD/UPDATE API 호출 핸들러 (API 호출 후 데이터 갱신)
const handleAdd = async () => {
  const addpriority: addpriority = {
    name: formData.value.name,
    level: formData.value.level
};
  UserStore.priorityPage.page = 1;
  isLoading.value = true;
  await onAddPriority(addpriority);
  await onReadPriorityList(UserStore.priorityPage).finally(() => {
    isLoading.value = false;
  });
  isDialogOpen.value = false;
};
const handleUpdate = async () => {
  isLoading.value = true;
  await onUpdatePriority(formData.value);
  await onReadPriorityList(UserStore.priorityPage).finally(() => {
    isLoading.value = false;
  });
  isDialogOpen.value = false;
};
const handleDelete = async () => {
  isLoading.value = true;
  await onDeletePriority(formData.value.id);
  await onReadPriorityList(UserStore.priorityPage).finally(() => {
    isLoading.value = false;
  });
  isDialogOpen.value = false;
};

// 페이지네이션 헨들러
const paginationHandler = async (page: number) => {
  if ( page < 1 ){
    page = 1;
  }
  else if ( page > totalpage.value ){
    page = totalpage.value;
  }
  currentPage.value = page;
  UserStore.priorityPage.page = currentPage.value;
  isLoading.value = true;
  await onReadPriorityList(UserStore.priorityPage).finally(() => {
    isLoading.value = false;
  });
};

// 컴포넌트 마운트 시 데이터 로드
onMounted(async() => {
  totalpage.value = await onPaginationPriority(UserStore.itemPerPage) ?? 1
  if (UserStore.priorityPage) {
    currentPage.value = UserStore.priorityPage.page;   
  }  
  await onReadPriorityList(UserStore.priorityPage).finally(() => {
    isLoading.value = false;
  });
});
</script>

<template>
  <div class="p-8">
    <!-- 헤더 -->
    <div class="flex items-center justify-between mb-4">
      <h1 class="text-3xl font-bold text-foreground">우선순위</h1>
      <Button @click="openAddDialog" :disabled="!UserStore.priorityAdd">
        <Plus class="mr-2 h-4 w-4" /> Add
      </Button>
    </div> 
    <!-- 데이터 테이블 -->
    <div class="border rounded-lg bg-card overflow-hidden text-foreground">
      <Table>
        <TableHeader>
          <TableRow class="bg-muted/50 hover:bg-muted/50">
            <TableHead class="font-semibold w-[400px] px-5">ID</TableHead>
            <TableHead class="font-semibold w-[400px]">Name</TableHead>
            <TableHead class="font-semibold w-[400px]">Priority</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <!-- 로딩 상태 Skeleton UI 적용-->
          <template v-if="isLoading">
            <TableRow v-for="i in UserStore.itemPerPage" :key="i">
              <TableCell colspan="3" class="px-3"><Skeleton class="h-5" /></TableCell>
            </TableRow>
          </template>
          <!-- 일반 상태 -->
          <template v-else>
            <TableRow v-for="priority in RSStore.priorityList" :key="priority.id" class="cursor-pointer hover:bg-accent/50"
              @click="openUpdateDialog(priority)">
              <TableCell class="px-5">{{ priority.id }}</TableCell>
              <TableCell>{{ priority.name }}</TableCell>
              <TableCell class="px-7">{{ priority.level }}</TableCell>
            </TableRow>
          <TableRow v-for="i in (UserStore.itemPerPage - RSStore.priorityList.length)" :key="'empty-' + i">
            <TableCell colspan="3">&nbsp;</TableCell>
          </TableRow>
            </template>
        </TableBody>
      </Table>
    </div>
    <!-- 페이지네이션 -->
    <div class="mt-4 flex justify-center">
      <Pagination v-model:page="currentPage" :total="totalpage * UserStore.itemPerPage" :items-per-page="UserStore.itemPerPage" :sibling-count="UserStore.siblingCount" show-edges>
        <PaginationContent v-slot="{ items }" class="flex items-center gap-1">
          <PaginationPrevious @click="paginationHandler(currentPage - RSStore.nextunit)" />
          <template v-for="(item, index) in items">
            <PaginationItem v-if="item.type === 'page'" :key="index" :value="item.value" as-child>
              <Button class="w-10 h-10 p-0" :variant="item.value === currentPage ? 'default' : 'outline'"
                @click="paginationHandler(item.value)">
                {{ item.value }}
              </Button>
            </PaginationItem>
            <PaginationEllipsis v-else :key="item.type" :index="index" />
          </template>
          <PaginationNext @click="paginationHandler(currentPage + RSStore.nextunit)" />
        </PaginationContent>
      </Pagination>
    </div>
    <!-- 추가/수정 다이얼로그 -->
    <Dialog v-model:open="isDialogOpen">
      <DialogContent class="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {{ isUpdateMode ? "우선순위 수정" : "우선순위 추가" }}
          </DialogTitle>
          <DialogDescription>
            {{ isUpdateMode ? "우선순위 정보를 수정하세요" : "새로운 우선순위를 입력하세요" }}
          </DialogDescription>
        </DialogHeader>
        <div class="grid gap-4 py-4 text-foreground">
          <div class="grid gap-2">
            <Label for="name">Name</Label>
            <Input id="name" v-model="formData.name" placeholder="우선순위 이름 입력" maxlength="16" />
          </div>
          <div class="grid gap-2">
            <Label for="priority">Priority</Label>
            <Input id="priority" type="number" v-model="formData.level" placeholder="우선순위 값 입력" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" @click="cancelDialog">Cancel</Button>
          <Button v-if="isUpdateMode" variant="destructive" @click="handleDelete()" :disabled="!UserStore.priorityDelete">
            Delete
          </Button>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger as-child>
                <div class="inline-block cursor-not-allowed">
                  <Button @click="isUpdateMode ? handleUpdate() : handleAdd()"
                    :disabled="isFormInvalid || (!UserStore.priorityEdit && isUpdateMode)" :class="{ 'pointer-events-none': isFormInvalid }">
                    {{ isUpdateMode ? "Update" : "Add" }}
                  </Button>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p v-if="isFormInvalid">name, Priority를 입력해주세요.</p>
                <p v-else>우선순위를 저장합니다.</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </div>
</template>